import { useState } from "react";
import { Outlet, NavLink, useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import {
  LayoutDashboard,
  Calendar,
  Users,
  BarChart3,
  Settings,
  Menu,
  LogOut,
  Stethoscope,
  ChevronLeft
} from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_3083dfc7-2383-45dd-896b-7ed8090288e6/artifacts/x64h80zt_WITAPP%20-%20Logo%20orizzontale%20-%20Colori%20-%20No%20claim.png";

const navItems = [
  { path: "/dashboard", label: "Dashboard", icon: LayoutDashboard, end: true },
  { path: "/dashboard/calendar", label: "Calendario", icon: Calendar },
  { path: "/dashboard/patients", label: "Pazienti", icon: Users },
  { path: "/dashboard/analytics", label: "Analytics", icon: BarChart3 },
];

const NavItem = ({ item, onClick }) => (
  <NavLink
    to={item.path}
    end={item.end}
    onClick={onClick}
    className={({ isActive }) =>
      cn(
        "flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm font-medium transition-all",
        isActive
          ? "bg-primary text-primary-foreground shadow-md"
          : "text-muted-foreground hover:bg-muted hover:text-foreground"
      )
    }
  >
    <item.icon className="h-5 w-5" />
    {item.label}
  </NavLink>
);

export const DashboardLayout = () => {
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleBackToApp = () => {
    navigate("/");
  };

  const SidebarContent = ({ onItemClick }) => (
    <div className="flex h-full flex-col">
      {/* Logo */}
      <div className="flex h-16 items-center border-b px-4">
        <img src={LOGO_URL} alt="WitApp" className="h-8 w-auto" />
      </div>

      {/* Navigation */}
      <ScrollArea className="flex-1 px-3 py-4">
        <nav className="space-y-1">
          {navItems.map((item) => (
            <NavItem key={item.path} item={item} onClick={onItemClick} />
          ))}
        </nav>
      </ScrollArea>

      {/* Footer */}
      <div className="border-t p-4 space-y-2">
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-muted-foreground"
          onClick={handleBackToApp}
        >
          <Stethoscope className="h-5 w-5" />
          Cambia profilo
        </Button>
        <Button
          variant="ghost"
          className="w-full justify-start gap-3 text-muted-foreground"
        >
          <Settings className="h-5 w-5" />
          Impostazioni
        </Button>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-background">
      {/* Desktop Sidebar */}
      <aside className="fixed inset-y-0 left-0 z-50 hidden w-64 border-r bg-card md:block">
        <SidebarContent />
      </aside>

      {/* Mobile Header */}
      <header className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b bg-card px-4 md:hidden">
        <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
          <SheetTrigger asChild>
            <Button variant="ghost" size="icon">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-64 p-0">
            <SidebarContent onItemClick={() => setMobileOpen(false)} />
          </SheetContent>
        </Sheet>
        <img src={LOGO_URL} alt="WitApp" className="h-8 w-auto" />
      </header>

      {/* Main Content */}
      <main className="md:pl-64">
        <div className="min-h-[calc(100vh-4rem)] md:min-h-screen">
          <Outlet />
        </div>
      </main>
    </div>
  );
};

export default DashboardLayout;
